import { defineMessages, type IntlShape } from 'react-intl';
import type { BlockSchemaProps } from '@plone/types';
import { defaultStylingSchema } from '../schema';

const messages = defineMessages({
  blockTitle: {
    id: 'Multi buttons block',
    defaultMessage: 'Botões (múltiplos)',
  },
  variant: {
    id: 'Multi buttons variant',
    defaultMessage: 'Variação',
  },
  variantHelp: {
    id: 'Multi buttons variant help',
    defaultMessage:
      'Escolha como os itens serão exibidos (botões, cards ou imagens).',
  },
  variantDefault: {
    id: 'Multi buttons variant default',
    defaultMessage: 'Botões (padrão)',
  },
  variantCard: {
    id: 'Multi buttons variant card',
    defaultMessage: 'Cards (estilo Listing Grid)',
  },
  variantImage: {
    id: 'Multi buttons variant image',
    defaultMessage: 'Imagens (botões por imagem)',
  },
  buttons: {
    id: 'Buttons',
    defaultMessage: 'Botões',
  },
  btnTitle: {
    id: 'Button label',
    defaultMessage: 'Label',
  },
  btnTitleA11y: {
    id: 'Button label a11y',
    defaultMessage: 'Rótulo (acessibilidade)',
  },
  btnDescription: {
    id: 'Button description',
    defaultMessage: 'Descrição',
  },
  btnHref: {
    id: 'Button link',
    defaultMessage: 'Link',
  },
  btnIcon: {
    id: 'Button icon',
    defaultMessage: 'Ícone',
  },
  btnImage: {
    id: 'Button image',
    defaultMessage: 'Imagem',
  },
  btnImageHelp: {
    id: 'Button image help',
    defaultMessage:
      'Selecione um item de imagem (ou um conteúdo com campo de imagem) para exibir como botão.',
  },
  btnAltText: {
    id: 'Button alt text',
    defaultMessage: 'Texto alternativo (alt)',
  },
  btnAltTextHelp: {
    id: 'Button alt text help',
    defaultMessage:
      'Obrigatório na variação de imagens. Descreva a ação do botão (ex.: “Acessar Transparência”).',
  },
  btnNewTab: {
    id: 'Open in a new tab',
    defaultMessage: 'Abrir em nova aba',
  },
  iconNone: {
    id: 'No icon',
    defaultMessage: 'Sem ícone',
  },
  iconArrow: {
    id: 'Arrow right',
    defaultMessage: 'Seta (direita)',
  },
  iconEmail: {
    id: 'Email',
    defaultMessage: 'E-mail',
  },
  iconPhone: {
    id: 'Phone',
    defaultMessage: 'Telefone',
  },
  iconUser: {
    id: 'User',
    defaultMessage: 'Usuário',
  },
  iconSummary: {
    id: 'Summary',
    defaultMessage: 'Resumo',
  },
  iconCallendar: {
    id: 'Calendar',
    defaultMessage: 'Calendário',
  },
  iconImage: {
    id: 'Image',
    defaultMessage: 'Imagem',
  },
  iconNews: {
    id: 'News',
    defaultMessage: 'Notícias',
  },
  iconLink: {
    id: 'Link',
    defaultMessage: 'Link',
  },
  iconCheck: {
    id: 'Check',
    defaultMessage: 'Check',
  },
  empty: {
    id: 'No buttons configured',
    defaultMessage: 'Nenhum botão configurado',
  },
  maxColumns: {
    id: 'Max columns',
    defaultMessage: 'Colunas (máximo por linha)',
  },
  maxColumnsHelp: {
    id: 'Max columns help',
    defaultMessage:
      'Limita quantos botões podem aparecer por linha. Em telas menores, o bloco sempre quebra para novas linhas automaticamente.',
  },
  stretch: {
    id: 'Stretch buttons',
    defaultMessage: 'Esticar para preencher a largura',
  },
  stretchHelp: {
    id: 'Stretch buttons help',
    defaultMessage:
      'Quando ativo, os botões usam larguras iguais para formar a quantidade de colunas escolhida (quando houver espaço). Quando desativado, os botões usam a largura do maior botão.',
  },
  themeColor: {
    id: 'Theme color',
    defaultMessage: 'Cor do tema',
  },
});

const buttonSchema = (intl: IntlShape, variant: string) => {
  const isCard = variant === 'card';
  const isImage = variant === 'image';

  const fields = isCard
    ? ['title', 'description', 'href', 'openInNewTab']
    : isImage
      ? ['title', 'image', 'altText', 'href', 'openInNewTab']
      : ['title', 'href', 'icon', 'openInNewTab'];

  return {
    title: intl.formatMessage(messages.buttons),
    fieldsets: [
      {
        id: 'default',
        title: 'default',
        fields,
      },
    ],
    properties: {
      title: {
        title: intl.formatMessage(
          isImage ? messages.btnTitleA11y : messages.btnTitle,
        ),
      },
      ...(isCard
        ? {
            description: {
              title: intl.formatMessage(messages.btnDescription),
              widget: 'textarea',
            },
          }
        : {}),
      href: {
        title: intl.formatMessage(messages.btnHref),
        widget: 'object_browser',
        mode: 'link',
        allowExternals: true,
        selectedItemAttrs: ['Title', '@type'],
      },
      ...(isImage
        ? {
            image: {
              title: intl.formatMessage(messages.btnImage),
              description: intl.formatMessage(messages.btnImageHelp),
              widget: 'object_browser',
              mode: 'link',
              allowExternals: true,
              selectedItemAttrs: ['Title', '@type'],
            },
            altText: {
              title: intl.formatMessage(messages.btnAltText),
              description: intl.formatMessage(messages.btnAltTextHelp),
            },
          }
        : {}),
      ...(!isCard && !isImage
        ? {
            icon: {
              title: intl.formatMessage(messages.btnIcon),
              type: 'string',
              choices: [
                ['', intl.formatMessage(messages.iconNone)],
                ['arrowRight', intl.formatMessage(messages.iconArrow)],
                ['phone', intl.formatMessage(messages.iconPhone)],
                ['user', intl.formatMessage(messages.iconUser)],
                ['summary', intl.formatMessage(messages.iconSummary)],
                ['callendar', intl.formatMessage(messages.iconCallendar)],
                ['image', intl.formatMessage(messages.iconImage)],
                ['news', intl.formatMessage(messages.iconNews)],
                ['link', intl.formatMessage(messages.iconLink)],
                ['check', intl.formatMessage(messages.iconCheck)],
                ['email', intl.formatMessage(messages.iconEmail)],
              ],
              default: '',
            },
          }
        : {}),
      openInNewTab: {
        title: intl.formatMessage(messages.btnNewTab),
        type: 'boolean',
      },
    },
    required: isImage ? ['image', 'altText'] : [],
  };
};

export const MultiButtonsBlockSchema = (props: BlockSchemaProps): any => {
  const { intl } = props;
  const formData = (props as any).formData ?? (props as any).data ?? props;
  const variant = (formData as any)?.variant || 'default';
  const buttonsCount = Array.isArray((formData as any)?.buttons)
    ? (formData as any).buttons.length
    : 0;

  const maxColumnsMax = Math.max(1, buttonsCount);

  const maxColumnsChoices: Array<[number, string]> = [
    ...Array.from(
      { length: maxColumnsMax },
      (_, idx) => [idx + 1, `${idx + 1}`] as [number, string],
    ),
  ];

  const schema: any = {
    title: intl.formatMessage(messages.blockTitle),
    fieldsets: [
      {
        id: 'default',
        title: 'default',
        fields: ['variant', 'buttons', 'maxColumns', 'stretch', 'themeColor'],
      },
    ],
    properties: {
      variant: {
        title: intl.formatMessage(messages.variant),
        description: intl.formatMessage(messages.variantHelp),
        type: 'string',
        default: 'default',
        choices: [
          ['default', intl.formatMessage(messages.variantDefault)],
          ['card', intl.formatMessage(messages.variantCard)],
          ['image', intl.formatMessage(messages.variantImage)],
        ],
      },
      buttons: {
        title: intl.formatMessage(messages.buttons),
        widget: 'object_list',
        schema: buttonSchema(intl, variant),
      },
      maxColumns: {
        title: intl.formatMessage(messages.maxColumns),
        description: intl.formatMessage(messages.maxColumnsHelp),
        type: 'number',
        default: maxColumnsMax,
        choices: maxColumnsChoices,
      },
      stretch: {
        title: intl.formatMessage(messages.stretch),
        description: intl.formatMessage(messages.stretchHelp),
        type: 'boolean',
        default: false,
      },
      themeColor: {
        title: intl.formatMessage(messages.themeColor),
        type: 'color',
        widget: 'color_picker',
      },
    },
    required: [],
  };

  return defaultStylingSchema({ schema, formData, intl });
};
